import { AdminDeal, ListParams, ListResult } from "../../types/admin";
import { supabase } from "../supabaseClient";

const DEAL_SELECT_QUERY = `
  id,
  created_at,
  updated_at,
  name,
  stage,
  currency,
  kpis,
  profiles(email, full_name) 
`;

const DEAL_INSPECT_QUERY = `
  id,
  created_at,
  updated_at,
  name,
  owner_id,
  currency,
  kpis,
  profiles(email, full_name) 
`;

export async function listDeals(
  params: ListParams = {},
): Promise<ListResult<AdminDeal>> {
  const page = params.page || 1;
  const pageSize = params.pageSize || 25;
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;

  let query = supabase
    .from("projects")
    .select(DEAL_SELECT_QUERY, { count: "exact" });

  if (params.userId) {
    query = query.eq("owner_id", params.userId);
  }

  if (params.search) {
    const searchPattern = `%${params.search || ""}%`;

    const searchString = `name.ilike.${searchPattern}`;

    query = query.or(searchString);
  }

  query = query.range(start, end);

  const { data, error, count } = await query;

  if (error) {
    console.error("Supabase listDeals error:", error);
    throw new Error("Failed to fetch projects from the database.");
  }

  const rows: AdminDeal[] = data.map((item: any) => {
    const kpis = item.kpis || {};

    const totalCost = kpis.purchasePrice || kpis.purchase?.price || 0;

    const projectIRR = kpis.financing?.interestRate / 100 || 0;

    return {
      id: item.id,
      title: item.name,
      stage: item.stage,
      currency: item.currency,
      userId: item.owner_id,

      createdAt: item.created_at,
      lastEditedAt: item.updated_at,

      userEmail: item.profiles?.email || "N/A",
      userDisplayName: item.profiles?.full_name || "N/A",

      totalProjectCost: totalCost,
      projectIRR: projectIRR,
    } as AdminDeal;
  });

  const total = count || 0;

  return {
    rows: rows,
    total: total,
    page: page,
    pageSize: pageSize,
    totalPages: Math.ceil(total / pageSize),
  };
}

export async function getDeal(id: string): Promise<AdminDeal | null> {
  const { data, error } = await supabase
    .from("projects")
    .select(DEAL_INSPECT_QUERY)
    .eq("id", id)
    .single();

  if (error) {
    console.error("[Supabase] getDeal error:", error);
    if (error.code === "PGRST116") {
      return null;
    }
    throw new Error("Failed to fetch deal details.");
  }

  if (!data) {
    return null;
  }

  const item = data as any;
  const kpis = item.kpis || {};
  const ownerProfile = item.profiles;

  const totalCost = kpis.purchasePrice || kpis.purchase?.price || 0;
  const projectIRR = kpis.ramp?.revenueGrowthY3Plus / 100 || 0;

  const deal = {
    id: item.id,
    title: item.name,
    currency: item.currency,

    createdAt: item.created_at,
    lastEditedAt: item.updated_at,

    userId: item.owner_id,
    userEmail: ownerProfile?.email || "N/A",
    userDisplayName: ownerProfile?.full_name || "N/A",

    totalProjectCost: totalCost,
    projectIRR: projectIRR,

    kpis: {
      rooms: kpis.rooms?.total || 0,
      adr: kpis.roomRev?.adr?.[0] || 0,
      occ: kpis.roomRev?.occ?.[0] || 0,
      revpar:
        ((kpis.roomRev?.adr?.[0] || 0) * (kpis.roomRev?.occ?.[0] || 0)) / 100,
    },
    payroll: kpis.payroll
      ? {
          totalAnnual:
            kpis.payroll.avgSalary *
              kpis.roomMix?.[0]?.count *
              kpis.payroll.ftePerRoom *
              12 *
              (1 + kpis.payroll.payrollTaxPct / 100) || 0,
          frontOffice: 0,
          housekeeping: 0,
          fnb: 0,
          management: 0,
        }
      : null,
    opex: kpis.opex,

    capex: kpis.budget
      ? {
          year0:
            kpis.budget.construction + kpis.budget.ffoeOse + kpis.budget.other,
          items: [{ name: "Construction", amount: kpis.budget.construction }],
        }
      : null,

    financing: kpis.financing
      ? {
          ltv: kpis.financing.debtToEquityRatio / 100,
          rate: kpis.financing.interestRate / 100,
          termYears: kpis.financing.loanTermYears,
        }
      : null,

    exit: kpis.exit
      ? {
          year: kpis.exit.holdPeriodYears,
          exitCapRate: kpis.exit.exitCapRate / 100,
          salePrice: totalCost * (kpis.exit.exitNOIMultiple || 1),
        }
      : null,
  };

  return deal;
}

export async function updateDeal(
  id: string,
  payload: Partial<AdminDeal>,
): Promise<AdminDeal> {
  const updates: any = {
    updated_at: new Date().toISOString(),
  };

  if (payload.userId !== undefined) updates.owner_id = payload.userId;

  const { error } = await supabase
    .from("projects")
    .update(updates)
    .eq("id", id);

  if (error) {
    console.error("Supabase updateDeal error:", error);
    throw new Error("Failed to update deal in database.");
  }

  const updatedDeal = await getDeal(id);

  if (!updatedDeal) {
    throw new Error("Deal not found after update.");
  }

  return updatedDeal;
}

export async function listAllDealsBasic(): Promise<
  { id: string; name: string }[]
> {
  const { data, error } = await supabase
    .from("projects")
    .select("id, name")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Supabase listAllDealsBasic error:", error);
    return [];
  }

  return data.map((item) => ({
    id: item.id,
    name: item.name || `Unnamed Project (${item.id.slice(0, 5)})`,
  }));
}
